import { SocialProvider } from '../types/auth';
import { config } from '../config';

/** postMessage type: popup callback → opener (must match SsoCallback). */
export const COGNITO_SSO_POPUP_MSG = 'EW_COGNITO_SSO_CALLBACK' as const;

export type CognitoSsoPopupMessage =
  | { type: typeof COGNITO_SSO_POPUP_MSG; code: string; state: string }
  | { type: typeof COGNITO_SSO_POPUP_MSG; error: string; error_description?: string };

const POPUP_FEATURES =
  'width=480,height=680,left=120,top=80,scrollbars=yes,resizable=yes,status=no';

/**
 * Build Cognito Hosted UI authorize URL (same as full-page redirect, but opened in a popup).
 */
export function buildCognitoHostedUiAuthorizeUrl(options: {
  provider: SocialProvider;
  state: string;
  origin?: string;
}): string {
  const baseOrigin = options.origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
  const redirectUri = `${baseOrigin}/auth/sso/callback`;
  const params = new URLSearchParams({
    client_id: config.sso_client_id,
    response_type: 'code',
    scope: 'openid email profile phone',
    redirect_uri: redirectUri,
    state: options.state,
    identity_provider: options.provider,
  });
  return `${config.sso_auth_url}?${params.toString()}`;
}

/**
 * Opens Cognito Hosted UI in a popup. Google (or other IdP) account selection happens inside the popup.
 * On success the callback page posts the Cognito authorization code to the opener and closes.
 */
/** After OAuth redirect, some browsers report `popup.closed` briefly while the popup navigates; don't treat that as cancel until it stays closed. */
const POPUP_CLOSED_CANCEL_MS = 1200;

export function openCognitoSsoPopup(authorizeUrl: string): Promise<{ code: string; state: string }> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let closedDebounce: ReturnType<typeof setTimeout> | null = null;
    const popup = window.open(authorizeUrl, 'ew_cognito_sso', POPUP_FEATURES);

    if (!popup) {
      reject(new Error('Popup was blocked. Allow popups for this site and try again.'));
      return;
    }

    const expectedOrigin = window.location.origin;

    const clearClosedDebounce = () => {
      if (closedDebounce !== null) {
        clearTimeout(closedDebounce);
        closedDebounce = null;
      }
    };

    const cleanup = (intervalId: ReturnType<typeof setInterval>) => {
      clearInterval(intervalId);
      clearClosedDebounce();
      window.removeEventListener('message', onMessage);
    };

    const finish = (
      intervalId: ReturnType<typeof setInterval>,
      fn: () => void,
    ) => {
      if (settled) return;
      settled = true;
      cleanup(intervalId);
      try {
        popup.close();
      } catch {
        /* ignore */
      }
      fn();
    };

    let closePoll: ReturnType<typeof setInterval>;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== expectedOrigin) return;
      const data = event.data as CognitoSsoPopupMessage | undefined;
      if (!data || data.type !== COGNITO_SSO_POPUP_MSG) return;

      if ('error' in data && data.error) {
        finish(closePoll, () =>
          reject(new Error(data.error_description || data.error || 'Sign-in failed')),
        );
        return;
      }
      if ('code' in data && data.code) {
        finish(closePoll, () =>
          resolve({ code: data.code, state: typeof data.state === 'string' ? data.state : '' }),
        );
      }
    };

    window.addEventListener('message', onMessage);

    closePoll = setInterval(() => {
      if (settled) return;
      if (popup.closed) {
        if (closedDebounce === null) {
          closedDebounce = setTimeout(() => {
            closedDebounce = null;
            if (settled) return;
            if (popup.closed) {
              finish(closePoll, () => reject(new Error('Sign-in was cancelled')));
            }
          }, POPUP_CLOSED_CANCEL_MS);
        }
      } else {
        clearClosedDebounce();
      }
    }, 400);
  });
}
